import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import {
  APPLICATION_NAMES,
  CACHED_LOCATION,
  DASHBOARD_URL,
  EPM_URL,
  ENCOMPASS_URL,
  EYEFINITY_URL,
  NavGuardRedirectWrapper,
  REDIRECT_FEATURE,
  SUPPORT_URL,
} from '@share/constants';
import { AuthenticationService } from '../../data-services/authentication/authentication.service';
import { NGXLogger } from 'ngx-logger';

/**
 * NavGuard is a way for users to redirect to specific pages within pe-conn by providing the right parameters.  This
 * gives static urls a way to navigate to pages within pe-conn by hitting the index.html that sites on s3.
 */
@Injectable()
export class NavGuard {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private logger: NGXLogger,
  ) {}

  /**
   * This will cancel the current navigation path and redirect to another path based on the presence of a feature
   * queryParameter
   * @param next
   * @param _state
   */
  canActivate(
    next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    const urlPath = this.getUrlPath(next);
    if (urlPath) {
      this.router.navigate([urlPath.path], {
        queryParams: { ...urlPath.queryParams },
      });
      return false;
    } else {
      const token = this.authenticationService.getPeConnAccessToken();
      const isEyefinityPractice =
        this.authenticationService.isEyefinityPractice();
      if (token && token.application) {
        const url = token.application.toLocaleLowerCase();
        const hasCompanyId = token.companyId;
        const hasEfan = token.efan;
        const isEmployee = url === APPLICATION_NAMES.pe;
        if (
          isEmployee &&
          !(hasCompanyId && hasEfan) &&
          !(isEyefinityPractice && hasEfan)
        ) {
          this.router.navigateByUrl('/' + url + SUPPORT_URL);
        } else if (
          url === APPLICATION_NAMES.epm ||
          (url === APPLICATION_NAMES.pe && hasCompanyId && hasEfan)
        ) {
          this.router.navigateByUrl(EPM_URL + DASHBOARD_URL);
        } else if (
          url === APPLICATION_NAMES.encompass ||
          (url === APPLICATION_NAMES.pe && hasCompanyId && hasEfan)
        ) {
          // Encompass ID currently does not support Active Directory credentials (support mode).
          // The 'or' condition remains for possible future support of AD credentials.
          this.router.navigateByUrl(ENCOMPASS_URL + DASHBOARD_URL);
        } else if (
          url === APPLICATION_NAMES.eyefinity ||
          (url === APPLICATION_NAMES.pe && isEyefinityPractice && hasEfan)
        ) {
          this.router.navigateByUrl(EYEFINITY_URL + DASHBOARD_URL);
        } else {
          this.router.navigateByUrl('/' + url + DASHBOARD_URL);
        }
        return false;
      }
    }
    return true;
  }

  /**
   * checks the url for queryParameter feature='/somePath'  If such a parameter exists this
   * method checks session storage for a path using the CACHED_LOCATION that should have been saved to the browser
   * prior to the application leaving pe-conn.
   * */
  getUrlPath(route: ActivatedRouteSnapshot): NavGuardRedirectWrapper {
    const navRedirect = {} as NavGuardRedirectWrapper;
    if (route && route.queryParams[REDIRECT_FEATURE]) {
      const redirectLocation: string =
        this.authenticationService.getFromSessionStorage(CACHED_LOCATION);
      if (redirectLocation) {
        navRedirect.path = redirectLocation;
      }
      navRedirect.queryParams = {
        ...route.queryParams,
      };
      this.authenticationService.removeFromStorage(REDIRECT_FEATURE);
      delete navRedirect.queryParams[REDIRECT_FEATURE];
      this.logger.debug('NavGuard redirecting to ', navRedirect.path);
      return navRedirect;
    }
    return null;
  }
}
